import React, { useRef, type KeyboardEvent } from 'react';
import { useTheme } from './ThemeProvider';
import useLavaLampIndicator from '../hooks/useLavaLampIndicator';

export default function ThemeSwitcher() {
  const { theme, setTheme, themes } = useTheme();
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const { containerRef, lavaLampHandlers } =
    useLavaLampIndicator<HTMLDivElement>({
      activeSelector: '.theme-option[data-active="true"]',
      itemSelector: '.theme-option',
    });

  const selectTheme = (index: number) => {
    const option = themes[index];

    if (!option) return;

    setTheme(option.id);
    optionRefs.current[index]?.focus();
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    let nextIndex: number | undefined;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (index + 1) % themes.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (index - 1 + themes.length) % themes.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = themes.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    selectTheme(nextIndex);
  };

  return (
    <div
      className="theme-switcher"
      role="radiogroup"
      aria-label="Theme"
      aria-orientation="horizontal"
      ref={containerRef}
      {...lavaLampHandlers}
    >
      <span className="lava-lamp-indicator" aria-hidden="true" />
      {themes.map((option, index) => (
        <button
          key={option.id}
          type="button"
          className="theme-option"
          role="radio"
          aria-checked={theme === option.id}
          aria-label={`${option.label} theme: ${option.description}`}
          data-active={theme === option.id}
          ref={(element) => {
            optionRefs.current[index] = element;
          }}
          tabIndex={theme === option.id ? 0 : -1}
          onClick={() => setTheme(option.id)}
          onKeyDown={(event) => handleKeyDown(event, index)}
        >
          <span className="theme-swatch" data-theme-swatch={option.id} aria-hidden="true" />
          <span>{option.label}</span>
        </button>
      ))}
    </div>
  );
}
